import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  ArrowLeft, Plus, Trash2, Printer, MessageCircle, Loader2, IndianRupee, Users, FileText, FileSpreadsheet, Wallet, Share2, Eye, RotateCcw,
} from "lucide-react";
import { BRAND } from "@/lib/brand";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "sw_admin_token";

const INVOICE_STATUS = {
  draft: { l: "Draft", c: "bg-white/10 text-white/70 border-white/20" },
  sent: { l: "Sent", c: "bg-brand-cyan/10 text-brand-cyan border-brand-cyan/40" },
  paid: { l: "Paid", c: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  overdue: { l: "Overdue", c: "bg-rose-500/20 text-rose-300 border-rose-400/40" },
};
const ESTIMATE_STATUS = {
  draft: { l: "Draft", c: "bg-white/10 text-white/70 border-white/20" },
  sent: { l: "Sent", c: "bg-brand-cyan/10 text-brand-cyan border-brand-cyan/40" },
  accepted: { l: "Accepted", c: "bg-emerald-500/15 text-emerald-300 border-emerald-400/40" },
  rejected: { l: "Rejected", c: "bg-rose-500/15 text-rose-300 border-rose-400/40" },
};

export default function Billing() {
  const token = sessionStorage.getItem(TOKEN_KEY);

  const [tab, setTab] = useState("invoices");
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [estimates, setEstimates] = useState([]);
  const [loading, setLoading] = useState(false);

  const [clientOpen, setClientOpen] = useState(false);
  const [editingClient, setEditingClient] = useState(null);
  const [docOpen, setDocOpen] = useState(false);
  const [docKind, setDocKind] = useState("invoice");
  const [editingDoc, setEditingDoc] = useState(null);
  const [catalog, setCatalog] = useState([]);
  const [expenses, setExpenses] = useState([]);
  const [expenseCats, setExpenseCats] = useState([]);
  const [staff, setStaff] = useState([]);
  const [expenseOpen, setExpenseOpen] = useState(false);
  const [editingExpense, setEditingExpense] = useState(null);
  const [catalogOpen, setCatalogOpen] = useState(false);
  const [editingCatalog, setEditingCatalog] = useState(null);
  const [viewClient, setViewClient] = useState(null);
  const [viewExpense, setViewExpense] = useState(null);
  const [repeatOpen, setRepeatOpen] = useState(false);
  const [repeatKind, setRepeatKind] = useState("invoice");
  const [expRepeatOpen, setExpRepeatOpen] = useState(false);
  const [filterMonth, setFilterMonth] = useState("all"); // "all" | "YYYY-MM"
  const [upi, setUpi] = useState({ upi_vpa: "", upi_name: "SECURE WATCH" });

  const hdrs = { headers: { "X-Admin-Token": token } };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [c, inv, est, cat, exp, cfg, ec, st] = await Promise.all([
        axios.get(`${API}/clients`, hdrs),
        axios.get(`${API}/billing?kind=invoice`, hdrs),
        axios.get(`${API}/billing?kind=estimate`, hdrs),
        axios.get(`${API}/catalog`, hdrs),
        axios.get(`${API}/expenses`, hdrs),
        axios.get(`${API}/billing/config`),
        axios.get(`${API}/expense-categories`, hdrs),
        axios.get(`${API}/staff`, hdrs),
      ]);
      setClients(c.data || []); setInvoices(inv.data || []); setEstimates(est.data || []);
      setCatalog(cat.data || []); setExpenses(exp.data || []); setUpi(cfg.data || upi);
      setExpenseCats(ec.data || []); setStaff(st.data || []);
    } catch { toast.error("Failed to load billing data"); }
    setLoading(false);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const clientMap = useMemo(
    () => Object.fromEntries(clients.map((c) => [c.id, c])), [clients]
  );

  const availableMonths = useMemo(() => {
    const s = new Set();
    for (const i of invoices) if (i.doc_date) s.add((i.doc_date || "").slice(0, 7));
    for (const i of estimates) if (i.doc_date) s.add((i.doc_date || "").slice(0, 7));
    for (const e of expenses) if (e.date) s.add((e.date || "").slice(0, 7));
    return Array.from(s).filter(Boolean).sort().reverse();
  }, [invoices, estimates, expenses]);

  if (!token) return <Navigate to="/admin" replace />;


  const overdueCount = invoices.filter((i) => i.status === "overdue").length;
  const totalDue = invoices.filter((i) => i.status !== "paid").reduce((a, b) => a + (b.total || 0), 0);
  const paidTotal = invoices.filter((i) => i.status === "paid").reduce((a, b) => a + (b.total || 0), 0);
  const totalInvoiced = invoices.reduce((a, b) => a + (b.total || 0), 0);
  const expenseTotal = expenses.reduce((a, e) => a + (Number(e.amount) || 0), 0);

  const matchesMonth = (dateStr) => filterMonth === "all" || (dateStr || "").startsWith(filterMonth);
  const filteredInvoices = invoices.filter((i) => matchesMonth(i.doc_date));
  const filteredEstimates = estimates.filter((i) => matchesMonth(i.doc_date));
  const filteredExpenses = expenses.filter((e) => matchesMonth(e.date));

  const saveClient = async (payload, id) => {
    try {
      if (id) {
        await axios.patch(`${API}/clients/${id}`, payload, hdrs);
        toast.success("Client updated");
      } else {
        await axios.post(`${API}/clients`, payload, hdrs);
        toast.success("Client added");
      }
      setClientOpen(false); setEditingClient(null);
      load();
    } catch { toast.error("Could not save client"); }
  };
  const removeClient = async (id) => {
    if (!window.confirm("Delete this client?")) return;
    try { await axios.delete(`${API}/clients/${id}`, hdrs); load(); toast.success("Client deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const addExpenseCategory = async (name) => {
    try {
      const res = await axios.post(`${API}/expense-categories`, { name }, hdrs);
      const list = await axios.get(`${API}/expense-categories`, hdrs);
      setExpenseCats(list.data || []);
      return res.data;
    } catch { toast.error("Could not add category"); return null; }
  };

  const addStaff = async (name) => {
    try {
      const res = await axios.post(`${API}/staff`, { name }, hdrs);
      const list = await axios.get(`${API}/staff`, hdrs);
      setStaff(list.data || []);
      return res.data;
    } catch { toast.error("Could not add staff"); return null; }
  };

  const saveDoc = async (payload, id) => {
    try {
      if (id) await axios.patch(`${API}/billing/${id}`, payload, hdrs);
      else await axios.post(`${API}/billing`, payload, hdrs);
      toast.success(`${payload.kind === "invoice" ? "Invoice" : "Estimate"} saved`);
      setDocOpen(false); setEditingDoc(null);
      load();
    } catch { toast.error("Could not save"); }
  };
  const changeStatus = async (id, status) => {
    try { await axios.patch(`${API}/billing/${id}`, { status }, hdrs); load(); }
    catch { toast.error("Could not update"); }
  };
  const removeDoc = async (id) => {
    if (!window.confirm("Delete this document?")) return;
    try { await axios.delete(`${API}/billing/${id}`, hdrs); load(); toast.success("Deleted"); }
    catch { toast.error("Could not delete"); }
  };

  const printDoc = (d) => {
    const c = clientMap[d.client_id] || {};
    const html = renderDocHtml(d, c, upi, false);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html); w.document.close();
  };

  const viewDoc = (d) => {
    const c = clientMap[d.client_id] || {};
    // forCanvas=true means "no auto-print" — user gets read-only preview
    const html = renderDocHtml(d, c, upi, true);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html); w.document.close();
  };

  const addExpense = async (payload, id) => {
    try {
      if (id) {
        await axios.patch(`${API}/expenses/${id}`, payload, hdrs);
        toast.success("Expense updated");
      } else {
        await axios.post(`${API}/expenses`, payload, hdrs);
        toast.success("Expense recorded");
      }
      setExpenseOpen(false); setEditingExpense(null);
      load();
    } catch { toast.error("Could not save expense"); }
  };
  const removeExpense = async (id) => {
    if (!window.confirm("Delete expense?")) return;
    try { await axios.delete(`${API}/expenses/${id}`, hdrs); load(); } catch { toast.error("Could not delete"); }
  };
  const addCatalog = async (payload, id) => {
    try {
      if (id) {
        await axios.patch(`${API}/catalog/${id}`, payload, hdrs);
        toast.success("Catalog item updated");
      } else {
        await axios.post(`${API}/catalog`, payload, hdrs);
        toast.success("Catalog item saved");
      }
      setCatalogOpen(false); setEditingCatalog(null);
      load();
    } catch { toast.error("Could not save"); }
  };
  const removeCatalog = async (id) => {
    try { await axios.delete(`${API}/catalog/${id}`, hdrs); load(); } catch { toast.error("Could not delete"); }
  };

  const repeatDocs = async ({ source_ids, target_date, target_due_date }) => {
    try {
      const res = await axios.post(`${API}/billing/repeat`, { source_ids, target_date, target_due_date: target_due_date || null }, hdrs);
      toast.success(`Created ${res.data?.count || 0} draft ${repeatKind}${res.data?.count === 1 ? "" : "s"}`);
      setRepeatOpen(false);
      load();
    } catch { toast.error("Could not duplicate"); }
  };

  const repeatExpenses = async ({ source_ids, target_date }) => {
    try {
      const res = await axios.post(`${API}/expenses/repeat`, { source_ids, target_date }, hdrs);
      toast.success(`Duplicated ${res.data?.count || 0} expense${res.data?.count === 1 ? "" : "s"}`);
      setExpRepeatOpen(false);
      load();
    } catch { toast.error("Could not duplicate expenses"); }
  };

  const remindDoc = (d) => {
    const c = clientMap[d.client_id] || {};
    const phone = (c.phone || "").replace(/\D/g, "").slice(-10);
    if (!phone) return toast.error("Client has no phone number");
    const msg =
      `Hello ${c.name?.split(" ")[0] || "there"},\n\nThis is a friendly reminder from Secure Watch. Your ` +
      `${d.kind} *${d.number}* dated ${d.doc_date}` +
      (d.due_date ? ` (due ${d.due_date})` : "") +
      ` for *₹${(d.total || 0).toLocaleString("en-IN")}* is currently ${d.status || "pending"}.\n\n` +
      `Please clear it at your earliest convenience.\n\nThank you,\nSecure Watch — Guwahati`;
    window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(msg)}`, "_blank", "noopener");
  };

  const shareDocImage = async (d) => {
    const c = clientMap[d.client_id] || {};
    const phone = (c.phone || "").replace(/\D/g, "").slice(-10);
    const html2canvas = (await import("html2canvas")).default;
    const wrap = document.createElement("div");
    wrap.style.cssText = "position:fixed;left:-10000px;top:0;width:800px;background:white;color:#111;";
    wrap.innerHTML = renderDocHtml(d, c, upi, /*forCanvas*/ true);
    document.body.appendChild(wrap);
    try {
      const canvas = await html2canvas(wrap, { scale: 2, backgroundColor: "#ffffff" });
      const blob = await new Promise((r) => canvas.toBlob(r, "image/png"));
      const file = new File([blob], `${d.number}.png`, { type: "image/png" });
      const text =
        `Hello ${c.name?.split(" ")[0] || "there"}, please find your ${d.kind} ${d.number} for ` +
        `₹${(d.total || 0).toLocaleString("en-IN")} attached. — Secure Watch`;

      if (navigator.canShare?.({ files: [file] }) && navigator.share) {
        await navigator.share({ files: [file], text, title: `${d.kind.toUpperCase()} ${d.number}` });
      } else {
        // Fallback: download the image, then open WhatsApp with text
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${d.number}.png`; a.click();
        URL.revokeObjectURL(url);
        if (phone) window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
        toast.success("Image downloaded. Attach it in WhatsApp when it opens.");
      }
    } catch {
      toast.error("Could not generate image");
    } finally {
      document.body.removeChild(wrap);
    }
  };

  return (
    <div className="min-h-screen bg-[#0A0A0B] text-white">
      <header className="border-b border-white/10 bg-black/60 backdrop-blur sticky top-0 z-10">
        <div className="mx-auto max-w-7xl px-5 lg:px-8 h-16 flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <Link to="/admin" data-testid="back-to-leads" className="p-2 -ml-2 rounded-md text-white/60 hover:text-white hover:bg-white/5"><ArrowLeft className="h-4 w-4" /></Link>
            <div className="h-9 w-9 rounded-md bg-white p-1 flex items-center justify-center">
              <img src={BRAND.logoUrl} alt="Secure Watch" className="h-full w-full object-contain" />
            </div>
            <div className="leading-none">
              <div className="font-heading font-extrabold text-white text-sm">SECURE WATCH · Billing</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-cyan">Invoices, Estimates &amp; Reminders</div>
            </div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-5 lg:px-8 py-8">
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3 mb-6">
          <StatCard label="Clients" value={clients.length} />
          <StatCard label="Invoices" value={invoices.length} />
          <StatCard label="Overdue" value={overdueCount} accent="rose" />
          <StatCard label="Outstanding ₹" value={totalDue.toLocaleString("en-IN")} accent="cyan" />
          <StatCard label="Paid ₹" value={paidTotal.toLocaleString("en-IN")} accent="emerald" />
          <StatCard label="Total ₹" value={totalInvoiced.toLocaleString("en-IN")} />
          <StatCard label="Expense ₹" value={expenseTotal.toLocaleString("en-IN")} accent="rose" />
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
            <TabsList className="bg-[#0F0F13] border border-white/10">
              <TabsTrigger value="invoices" data-testid="tab-invoices"><FileText className="h-3.5 w-3.5 mr-1.5" />Invoices</TabsTrigger>
              <TabsTrigger value="estimates" data-testid="tab-estimates"><FileSpreadsheet className="h-3.5 w-3.5 mr-1.5" />Estimates</TabsTrigger>
              <TabsTrigger value="clients" data-testid="tab-clients"><Users className="h-3.5 w-3.5 mr-1.5" />Clients</TabsTrigger>
              <TabsTrigger value="catalog" data-testid="tab-catalog"><IndianRupee className="h-3.5 w-3.5 mr-1.5" />Catalog</TabsTrigger>
              <TabsTrigger value="expenses" data-testid="tab-expenses"><Wallet className="h-3.5 w-3.5 mr-1.5" />Expenses</TabsTrigger>
              <TabsTrigger value="reports" data-testid="tab-reports"><FileText className="h-3.5 w-3.5 mr-1.5" />Reports</TabsTrigger>
            </TabsList>
            <div className="flex gap-2 flex-wrap items-end">
              {(tab === "invoices" || tab === "estimates" || tab === "expenses") && (
                <div>
                  <Label className="text-white/60 text-[10px] uppercase tracking-widest">Month</Label>
                  <Select value={filterMonth} onValueChange={setFilterMonth}>
                    <SelectTrigger data-testid="month-filter" className="bg-black/40 border-white/10 h-9 w-40 text-white text-sm">
                      <SelectValue placeholder="All months" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                      <SelectItem value="all">All months</SelectItem>
                      {availableMonths.map((m) => (
                        <SelectItem key={m} value={m}>{monthLabel(m)}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {tab === "clients" && (<Button size="sm" onClick={() => { setEditingClient(null); setClientOpen(true); }} data-testid="add-client-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-semibold"><Plus className="h-3.5 w-3.5 mr-1" />New client</Button>)}
              {tab === "catalog" && (<Button size="sm" onClick={() => { setEditingCatalog(null); setCatalogOpen(true); }} data-testid="add-catalog-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-semibold"><Plus className="h-3.5 w-3.5 mr-1" />New item</Button>)}
              {tab === "expenses" && (
                <>
                  <Button size="sm" variant="outline" data-testid="repeat-expenses-btn" onClick={() => setExpRepeatOpen(true)} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Repeat month
                  </Button>
                  <Button size="sm" onClick={() => { setEditingExpense(null); setExpenseOpen(true); }} data-testid="add-expense-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-semibold"><Plus className="h-3.5 w-3.5 mr-1" />New expense</Button>
                </>
              )}
              {(tab === "invoices" || tab === "estimates") && (
                <>
                  <Button size="sm" variant="outline" data-testid={`repeat-${tab}-btn`} onClick={() => { setRepeatKind(tab === "invoices" ? "invoice" : "estimate"); setRepeatOpen(true); }} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 font-semibold">
                    <RotateCcw className="h-3.5 w-3.5 mr-1" />Repeat month
                  </Button>
                  <Button size="sm" data-testid={`add-${tab}-btn`} onClick={() => { setDocKind(tab === "invoices" ? "invoice" : "estimate"); setEditingDoc(null); setDocOpen(true); }} className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-semibold">
                    <Plus className="h-3.5 w-3.5 mr-1" />New {tab === "invoices" ? "invoice" : "estimate"}
                  </Button>
                </>
              )}
            </div>
          </div>

          <TabsContent value="invoices">
            <DocTable docs={filteredInvoices} clients={clientMap} statusMap={INVOICE_STATUS}
              onView={viewDoc}
              onEdit={(d) => { setDocKind("invoice"); setEditingDoc(d); setDocOpen(true); }}
              onStatus={changeStatus} onPrint={printDoc} onRemind={remindDoc} onShareImg={shareDocImage} onDelete={removeDoc} />
          </TabsContent>
          <TabsContent value="estimates">
            <DocTable docs={filteredEstimates} clients={clientMap} statusMap={ESTIMATE_STATUS}
              onView={viewDoc}
              onEdit={(d) => { setDocKind("estimate"); setEditingDoc(d); setDocOpen(true); }}
              onStatus={changeStatus} onPrint={printDoc} onRemind={remindDoc} onShareImg={shareDocImage} onDelete={removeDoc} />
          </TabsContent>
          <TabsContent value="clients">
            <ClientTable clients={clients} onView={setViewClient} onEdit={(cl) => { setEditingClient(cl); setClientOpen(true); }} onDelete={removeClient} />
          </TabsContent>
          <TabsContent value="catalog">
            <CatalogTable items={catalog} onEdit={(it) => { setEditingCatalog(it); setCatalogOpen(true); }} onDelete={removeCatalog} />
          </TabsContent>
          <TabsContent value="expenses">
            <ExpensesTable expenses={filteredExpenses} onView={setViewExpense} onEdit={(e) => { setEditingExpense(e); setExpenseOpen(true); }} onDelete={removeExpense} />
          </TabsContent>
          <TabsContent value="reports">
            <PnLReport token={token} clientMap={clientMap} />
          </TabsContent>
        </Tabs>

        {loading && <div className="mt-6 text-center text-white/50 text-sm"><Loader2 className="inline h-4 w-4 animate-spin mr-2" />Loading…</div>}
        {invoices.length === 0 && tab === "invoices" && !loading && <Empty text="No invoices yet. Click 'New invoice' to create one." />}
        {estimates.length === 0 && tab === "estimates" && !loading && <Empty text="No estimates yet." />}
        {clients.length === 0 && tab === "clients" && !loading && <Empty text="No clients yet. Add one to start invoicing." />}
      </main>

      <ClientDialog open={clientOpen} editing={editingClient} onOpenChange={(v) => { setClientOpen(v); if (!v) setEditingClient(null); }} onSave={saveClient} />
      <DocDialog open={docOpen} onOpenChange={(v) => { setDocOpen(v); if (!v) setEditingDoc(null); }}
        kind={docKind} clients={clients} catalog={catalog} editing={editingDoc} onSave={saveDoc} />
      <CatalogDialog open={catalogOpen} editing={editingCatalog} onOpenChange={(v) => { setCatalogOpen(v); if (!v) setEditingCatalog(null); }} onSave={addCatalog} />
      <ExpenseDialog open={expenseOpen} editing={editingExpense} onOpenChange={(v) => { setExpenseOpen(v); if (!v) setEditingExpense(null); }} onSave={addExpense} categories={expenseCats} onAddCategory={addExpenseCategory} staffList={staff} onAddStaff={addStaff} />
      <ClientViewDialog open={!!viewClient} client={viewClient} invoices={invoices} onOpenChange={(v) => !v && setViewClient(null)} />
      <ExpenseViewDialog open={!!viewExpense} expense={viewExpense} onOpenChange={(v) => !v && setViewExpense(null)} />
      <RepeatMonthDialog open={repeatOpen} kind={repeatKind} docs={repeatKind === "invoice" ? invoices : estimates} clients={clientMap} onOpenChange={setRepeatOpen} onSubmit={repeatDocs} />
      <RepeatExpensesDialog open={expRepeatOpen} expenses={expenses} onOpenChange={setExpRepeatOpen} onSubmit={repeatExpenses} />
    </div>
  );
}

function StatCard({ label, value, accent }) {
  const cx = accent === "rose" ? "text-rose-400"
    : accent === "cyan" ? "text-brand-cyan text-glow"
    : accent === "emerald" ? "text-emerald-400"
    : "text-white";
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-4">
      <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">{label}</div>
      <div className={`font-heading text-3xl font-extrabold mt-1 ${cx}`}>{value}</div>
    </div>
  );
}

function Empty({ text }) {
  return <div className="mt-6 rounded-xl border border-dashed border-white/10 p-10 text-center text-white/50">{text}</div>;
}

function DocTable({ docs, clients, statusMap, onView, onEdit, onStatus, onPrint, onRemind, onShareImg, onDelete }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-white/60">Number</TableHead>
            <TableHead className="text-white/60">Client</TableHead>
            <TableHead className="text-white/60 hidden md:table-cell">Date</TableHead>
            <TableHead className="text-white/60 hidden lg:table-cell">Due</TableHead>
            <TableHead className="text-white/60 text-right">Total</TableHead>
            <TableHead className="text-white/60">Status</TableHead>
            <TableHead className="text-white/60 text-right">Actions</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {docs.map((d) => {
            const c = clients[d.client_id] || {};
            const st = statusMap[d.status] || { l: d.status, c: "bg-white/10 text-white/60 border-white/15" };
            return (
              <TableRow key={d.id} className="border-white/10 hover:bg-white/5" data-testid={`doc-row-${d.id}`}>
                <TableCell className="font-mono text-brand-cyan text-xs">{d.number}</TableCell>
                <TableCell className="text-white font-semibold">{c.name || "—"}<div className="text-[10px] text-white/40">{c.phone}</div></TableCell>
                <TableCell className="text-white/70 hidden md:table-cell text-xs">{d.doc_date}</TableCell>
                <TableCell className="text-white/70 hidden lg:table-cell text-xs">{d.due_date || "—"}</TableCell>
                <TableCell className="text-right text-white font-semibold whitespace-nowrap">₹{(d.total || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell>
                  <Select value={d.status} onValueChange={(v) => onStatus(d.id, v)}>
                    <SelectTrigger className={`h-7 w-32 text-xs border ${st.c}`}><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                      {Object.entries(statusMap).map(([k, s]) => <SelectItem key={k} value={k}>{s.l}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </TableCell>
                <TableCell>
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onView(d)} data-testid={`view-doc-${d.id}`} title="View" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center"><Eye className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onEdit(d)} data-testid={`edit-doc-${d.id}`} title="Edit" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center text-xs">✎</button>
                    <button onClick={() => onPrint(d)} data-testid={`print-doc-${d.id}`} title="Print" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center"><Printer className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onRemind(d)} title="Send WhatsApp text reminder" className="h-7 w-7 rounded-md bg-[#25D366]/20 border border-[#25D366]/40 text-[#25D366] hover:bg-[#25D366]/25 flex items-center justify-center"><MessageCircle className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onShareImg(d)} title="Share as image on WhatsApp" className="h-7 w-7 rounded-md bg-[#25D366]/30 border border-[#25D366]/60 text-white hover:bg-[#25D366]/45 flex items-center justify-center"><Share2 className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onDelete(d.id)} title="Delete" className="h-7 w-7 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}

function ClientTable({ clients, onView, onEdit, onDelete }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-white/60">Name</TableHead>
            <TableHead className="text-white/60">Phone</TableHead>
            <TableHead className="text-white/60 hidden md:table-cell">Plan</TableHead>
            <TableHead className="text-white/60 text-right">Monthly ₹</TableHead>
            <TableHead className="text-white/60 text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {clients.map((c) => (
            <TableRow key={c.id} className="border-white/10 hover:bg-white/5" data-testid={`client-row-${c.id}`}>
              <TableCell className="text-white font-semibold">{c.name}{c.is_monitored === false && <span className="ml-2 inline-block text-[9px] uppercase tracking-widest bg-amber-500/15 text-amber-300 border border-amber-500/30 rounded px-1.5 py-0.5">No monitor</span>}<div className="text-[10px] text-white/40">{c.email || c.address || ""}</div></TableCell>
              <TableCell className="text-white/80 font-mono">{c.phone}</TableCell>
              <TableCell className="text-white/70 hidden md:table-cell">{c.plan || "—"}</TableCell>
              <TableCell className="text-right text-white/80">₹{(c.monthly_amount || 0).toLocaleString("en-IN")}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => onView(c)} data-testid={`view-client-${c.id}`} title="View" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center"><Eye className="h-3.5 w-3.5" /></button>
                  <button onClick={() => onEdit(c)} data-testid={`edit-client-${c.id}`} title="Edit" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center text-xs">✎</button>
                  <button onClick={() => onDelete(c.id)} data-testid={`delete-client-${c.id}`} title="Delete" className="h-7 w-7 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ClientDialog({ open, editing, onOpenChange, onSave }) {
  const empty = { name: "", phone: "", email: "", address: "", plan: "", monthly_amount: 0, notes: "", total_cameras: 0, camera_names: [], is_monitored: true };
  const [f, setF] = useState(empty);
  const [newCam, setNewCam] = useState("");
  useEffect(() => {
    if (!open) return;
    setF(editing ? { ...empty, ...editing, monthly_amount: editing.monthly_amount || 0, total_cameras: editing.total_cameras || 0, camera_names: Array.isArray(editing.camera_names) ? editing.camera_names : [], is_monitored: editing.is_monitored !== false } : empty);
    setNewCam("");
    // eslint-disable-next-line
  }, [open, editing]);
  const addCam = () => {
    const v = newCam.trim();
    if (!v) return;
    if (f.camera_names.includes(v)) return;
    setF({ ...f, camera_names: [...f.camera_names, v] });
    setNewCam("");
  };
  const removeCam = (name) => setF({ ...f, camera_names: f.camera_names.filter((c) => c !== name) });
  const submit = (e) => {
    e.preventDefault();
    if (!f.name || !f.phone) return toast.error("Name and phone required");
    // Auto-sync total_cameras with camera_names list length when list is non-empty
    const total = f.camera_names.length > 0 ? f.camera_names.length : (Number(f.total_cameras) || 0);
    onSave({ ...f, monthly_amount: Number(f.monthly_amount) || 0, total_cameras: total, is_monitored: !!f.is_monitored }, editing?.id);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-md max-h-[92vh] overflow-y-auto">
        <DialogHeader><DialogTitle>{editing ? "Edit client" : "New client"}</DialogTitle><DialogDescription>{editing ? "Update client details." : "Add a paying client to bill later."}</DialogDescription></DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <FormRow label="Name *"><Input data-testid="client-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="bg-black/40 border-white/10" /></FormRow>
          <FormRow label="Phone *"><Input data-testid="client-phone" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} className="bg-black/40 border-white/10" /></FormRow>
          <FormRow label="Email"><Input data-testid="client-email" value={f.email || ""} onChange={(e) => setF({ ...f, email: e.target.value })} className="bg-black/40 border-white/10" /></FormRow>
          <FormRow label="Address"><Textarea rows={2} value={f.address || ""} onChange={(e) => setF({ ...f, address: e.target.value })} className="bg-black/40 border-white/10" /></FormRow>
          <div className="grid grid-cols-3 gap-3">
            <FormRow label="Plan"><Input value={f.plan || ""} onChange={(e) => setF({ ...f, plan: e.target.value })} placeholder="Starter/Shield/…" className="bg-black/40 border-white/10" /></FormRow>
            <FormRow label="Monthly ₹"><Input type="number" value={f.monthly_amount} onChange={(e) => setF({ ...f, monthly_amount: e.target.value })} className="bg-black/40 border-white/10" /></FormRow>
            <FormRow label="Cameras"><Input data-testid="client-cameras" type="number" value={f.camera_names.length > 0 ? f.camera_names.length : f.total_cameras} onChange={(e) => setF({ ...f, total_cameras: e.target.value })} disabled={f.camera_names.length > 0} className="bg-black/40 border-white/10 disabled:opacity-60" /></FormRow>
          </div>
          <label className="flex items-center gap-2 cursor-pointer text-sm bg-black/30 border border-white/10 rounded-md px-3 py-2">
            <input data-testid="client-is-monitored" type="checkbox" checked={f.is_monitored} onChange={(e) => setF({ ...f, is_monitored: e.target.checked })} className="accent-brand-cyan h-4 w-4" />
            <span className="text-white">Include in Night Monitoring</span>
            <span className="ml-auto text-[10px] text-white/40 uppercase tracking-widest">{f.is_monitored ? "Active" : "Excluded"}</span>
          </label>
          <div>
            <Label className="text-white/80 text-xs">Camera names <span className="text-white/40">(used in night monitoring dropdown)</span></Label>
            <div className="flex gap-2 mt-1">
              <Input data-testid="client-new-camera" value={newCam} onChange={(e) => setNewCam(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCam(); } }} placeholder="e.g. CAM 01, Entrance, Warehouse-Cam 3" className="bg-black/40 border-white/10" />
              <Button type="button" onClick={addCam} data-testid="client-add-camera-btn" className="bg-brand-cyan/20 border border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/30"><Plus className="h-3.5 w-3.5 mr-1" />Add</Button>
            </div>
            {f.camera_names.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2" data-testid="camera-name-chips">
                {f.camera_names.map((cam) => (
                  <span key={cam} className="inline-flex items-center gap-1 rounded-full bg-white/5 border border-white/15 px-2.5 py-1 text-xs text-white">
                    {cam}
                    <button type="button" onClick={() => removeCam(cam)} data-testid={`remove-camera-${cam}`} className="text-white/40 hover:text-rose-300">✕</button>
                  </span>
                ))}
              </div>
            )}
            {f.camera_names.length > 0 && (
              <div className="text-[10px] text-white/40 mt-1">Total cameras auto-set to {f.camera_names.length} from list above.</div>
            )}
          </div>
          <DialogFooter><Button type="submit" data-testid="client-save-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">{editing ? "Update client" : "Save client"}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function FormRow({ label, children }) {
  return <div><Label className="text-white/80 text-xs">{label}</Label>{children}</div>;
}

function CatalogTable({ items, onEdit, onDelete }) {
  if (!items?.length) return <Empty text="No catalog items yet. Add reusable line items to speed up invoicing." />;
  return (
    <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-white/10 hover:bg-transparent">
            <TableHead className="text-white/60">Name</TableHead>
            <TableHead className="text-white/60 hidden sm:table-cell">Unit</TableHead>
            <TableHead className="text-white/60 text-right">Default Rate ₹</TableHead>
            <TableHead className="text-white/60 text-right"></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {items.map((it) => (
            <TableRow key={it.id} className="border-white/10 hover:bg-white/5" data-testid={`catalog-row-${it.id}`}>
              <TableCell className="text-white font-semibold">{it.name}</TableCell>
              <TableCell className="text-white/70 hidden sm:table-cell">{it.unit || "—"}</TableCell>
              <TableCell className="text-right text-white/85">₹{(it.default_rate || 0).toLocaleString("en-IN")}</TableCell>
              <TableCell className="text-right">
                <div className="flex items-center justify-end gap-1">
                  <button onClick={() => onEdit(it)} data-testid={`edit-catalog-${it.id}`} title="Edit" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center text-xs">✎</button>
                  <button onClick={() => onDelete(it.id)} data-testid={`delete-catalog-${it.id}`} title="Delete" className="h-7 w-7 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
}

function ExpensesTable({ expenses, onView, onEdit, onDelete }) {
  if (!expenses?.length) return <Empty text="No expenses recorded yet." />;
  const total = expenses.reduce((a, e) => a + (Number(e.amount) || 0), 0);
  return (
    <div className="space-y-3">
      <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-white/10 hover:bg-transparent">
              <TableHead className="text-white/60">Date</TableHead>
              <TableHead className="text-white/60">Category</TableHead>
              <TableHead className="text-white/60 hidden lg:table-cell">Staff</TableHead>
              <TableHead className="text-white/60 hidden md:table-cell">Note</TableHead>
              <TableHead className="text-white/60 hidden sm:table-cell">Paid via</TableHead>
              <TableHead className="text-white/60 text-right">Amount ₹</TableHead>
              <TableHead className="text-white/60 text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {expenses.map((e) => (
              <TableRow key={e.id} className="border-white/10 hover:bg-white/5" data-testid={`expense-row-${e.id}`}>
                <TableCell className="text-white/80 text-xs">{e.date}</TableCell>
                <TableCell className="text-white font-semibold">{e.category}</TableCell>
                <TableCell className="text-white/75 hidden lg:table-cell">{e.staff || "—"}</TableCell>
                <TableCell className="text-white/70 hidden md:table-cell text-xs">{e.note || "—"}</TableCell>
                <TableCell className="text-white/70 hidden sm:table-cell">{e.paid_via || "—"}</TableCell>
                <TableCell className="text-right text-rose-300 font-semibold whitespace-nowrap">-₹{(e.amount || 0).toLocaleString("en-IN")}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <button onClick={() => onView(e)} data-testid={`view-expense-${e.id}`} title="View" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center"><Eye className="h-3.5 w-3.5" /></button>
                    <button onClick={() => onEdit(e)} data-testid={`edit-expense-${e.id}`} title="Edit" className="h-7 w-7 rounded-md bg-white/5 border border-white/15 text-white hover:bg-white/10 flex items-center justify-center text-xs">✎</button>
                    <button onClick={() => onDelete(e.id)} data-testid={`delete-expense-${e.id}`} title="Delete" className="h-7 w-7 rounded-md bg-rose-500/10 border border-rose-500/30 text-rose-300 hover:bg-rose-500/20 flex items-center justify-center"><Trash2 className="h-3.5 w-3.5" /></button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <div className="text-right text-xs text-white/50">
        Total spend: <span className="text-rose-300 font-bold">₹{total.toLocaleString("en-IN")}</span>
      </div>
    </div>
  );
}

function CatalogDialog({ open, editing, onOpenChange, onSave }) {
  const empty = { name: "", default_rate: 0, unit: "" };
  const [f, setF] = useState(empty);
  useEffect(() => {
    if (!open) return;
    setF(editing ? { name: editing.name || "", default_rate: editing.default_rate || 0, unit: editing.unit || "" } : empty);
    // eslint-disable-next-line
  }, [open, editing]);
  const submit = (e) => {
    e.preventDefault();
    if (!f.name.trim()) return toast.error("Name is required");
    onSave({ name: f.name.trim(), default_rate: Number(f.default_rate) || 0, unit: f.unit || null }, editing?.id);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit catalog item" : "New catalog item"}</DialogTitle>
          <DialogDescription>Save reusable line items to speed up invoice creation.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <FormRow label="Name *">
            <Input data-testid="catalog-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="e.g. Monthly CCTV Remote Monitoring" className="bg-black/40 border-white/10" />
          </FormRow>
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Default rate ₹">
              <Input data-testid="catalog-rate" type="number" value={f.default_rate} onChange={(e) => setF({ ...f, default_rate: e.target.value })} className="bg-black/40 border-white/10" />
            </FormRow>
            <FormRow label="Unit">
              <Input data-testid="catalog-unit" value={f.unit} onChange={(e) => setF({ ...f, unit: e.target.value })} placeholder="month / camera / visit" className="bg-black/40 border-white/10" />
            </FormRow>
          </div>
          <DialogFooter>
            <Button type="submit" data-testid="catalog-save-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">{editing ? "Update item" : "Save item"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ExpenseDialog({ open, editing, onOpenChange, onSave, categories = [], onAddCategory, staffList = [], onAddStaff }) {
  const today = new Date().toISOString().slice(0, 10);
  const empty = { date: today, category: "", amount: 0, note: "", paid_via: "", staff: "" };
  const [f, setF] = useState(empty);
  const [newCat, setNewCat] = useState("");
  const [addingCat, setAddingCat] = useState(false);
  const [newStaff, setNewStaff] = useState("");
  const [addingStaff, setAddingStaff] = useState(false);
  useEffect(() => {
    if (!open) return;
    setF(editing ? { date: editing.date || today, category: editing.category || "", amount: editing.amount || 0, note: editing.note || "", paid_via: editing.paid_via || "", staff: editing.staff || "" } : empty);
    setNewCat(""); setAddingCat(false); setNewStaff(""); setAddingStaff(false);
    // eslint-disable-next-line
  }, [open, editing]);
  const addNewCategory = async () => {
    const name = newCat.trim();
    if (!name) return;
    const created = await onAddCategory?.(name);
    if (created) { setF((s) => ({ ...s, category: created.name })); setNewCat(""); setAddingCat(false); }
  };
  const addNewStaff = async () => {
    const name = newStaff.trim();
    if (!name) return;
    const created = await onAddStaff?.(name);
    if (created) { setF((s) => ({ ...s, staff: created.name })); setNewStaff(""); setAddingStaff(false); }
  };
  const submit = (e) => {
    e.preventDefault();
    if (!f.category.trim() || !f.amount) return toast.error("Category and amount required");
    onSave({ date: f.date, category: f.category.trim(), amount: Number(f.amount) || 0, note: f.note || null, paid_via: f.paid_via || null, staff: f.staff || null }, editing?.id);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{editing ? "Edit expense" : "New expense"}</DialogTitle>
          <DialogDescription>Track business spend so P&amp;L stays accurate.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="grid gap-3">
          <div className="grid grid-cols-2 gap-3">
            <FormRow label="Date">
              <Input data-testid="expense-date" type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} className="bg-black/40 border-white/10" />
            </FormRow>
            <FormRow label="Amount ₹ *">
              <Input data-testid="expense-amount" type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="bg-black/40 border-white/10" />
            </FormRow>
          </div>
          <FormRow label="Category *">
            {addingCat ? (
              <div className="flex gap-2">
                <Input data-testid="expense-new-category" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category name" className="bg-black/40 border-white/10" autoFocus />
                <Button type="button" data-testid="expense-new-category-save" onClick={addNewCategory} className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">Add</Button>
                <Button type="button" variant="ghost" onClick={() => { setAddingCat(false); setNewCat(""); }} className="text-white/60">✕</Button>
              </div>
            ) : (
              <div className="flex gap-2">
                <div className="flex-1">
                  <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}>
                    <SelectTrigger data-testid="expense-category-select" className="bg-black/40 border-white/10"><SelectValue placeholder="Select category" /></SelectTrigger>
                    <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                      {categories.map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <Button type="button" data-testid="expense-add-category-btn" onClick={() => setAddingCat(true)} className="bg-white/5 border border-white/15 text-white hover:bg-white/10"><Plus className="h-3.5 w-3.5 mr-1" />New</Button>
              </div>
            )}
          </FormRow>
          {f.category && (
            <FormRow label="Staff (optional)">
              {addingStaff ? (
                <div className="flex gap-2">
                  <Input data-testid="expense-new-staff" value={newStaff} onChange={(e) => setNewStaff(e.target.value)} placeholder="Staff name" className="bg-black/40 border-white/10" autoFocus />
                  <Button type="button" data-testid="expense-new-staff-save" onClick={addNewStaff} className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">Add</Button>
                  <Button type="button" variant="ghost" onClick={() => { setAddingStaff(false); setNewStaff(""); }} className="text-white/60">✕</Button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <div className="flex-1">
                    <Select value={f.staff} onValueChange={(v) => setF({ ...f, staff: v })}>
                      <SelectTrigger data-testid="expense-staff-select" className="bg-black/40 border-white/10"><SelectValue placeholder="Select staff" /></SelectTrigger>
                      <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                        {staffList.map((s) => <SelectItem key={s.id} value={s.name}>{s.name}{s.role ? ` · ${s.role}` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button type="button" data-testid="expense-add-staff-btn" onClick={() => setAddingStaff(true)} className="bg-white/5 border border-white/15 text-white hover:bg-white/10"><Plus className="h-3.5 w-3.5 mr-1" />New</Button>
                </div>
              )}
            </FormRow>
          )}
          <FormRow label="Paid via">
            <Input data-testid="expense-paid-via" value={f.paid_via} onChange={(e) => setF({ ...f, paid_via: e.target.value })} placeholder="Cash / UPI / Card" className="bg-black/40 border-white/10" />
          </FormRow>
          <FormRow label="Note">
            <Textarea data-testid="expense-note" rows={2} value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} className="bg-black/40 border-white/10" />
          </FormRow>
          <DialogFooter>
            <Button type="submit" data-testid="expense-save-btn" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">{editing ? "Update expense" : "Save expense"}</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function PnLReport({ token, clientMap }) {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);

  const fetchReport = async (m = month) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/reports/pnl?month=${m}`, { headers: { "X-Admin-Token": token } });
      setData(res.data);
    } catch { toast.error("Could not load report"); }
    setBusy(false);
  };
  useEffect(() => { fetchReport(defaultMonth); /* eslint-disable-next-line */ }, []);

  const printReport = () => {
    if (!data) return;
    const html = renderPnLHtml(data, clientMap);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html); w.document.close();
  };

  const viewReport = () => {
    if (!data) return;
    const html = renderPnLHtml(data, clientMap, /*forCanvas*/ true);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) return toast.error("Popup blocked");
    w.document.write(html); w.document.close();
  };

  const openMonthly = async (mode /* 'view' | 'print' */) => {
    setBusy(true);
    try {
      const res = await axios.get(`${API}/reports/monthly?month=${month}`, { headers: { "X-Admin-Token": token } });
      const html = renderMonthlyHtml(res.data, clientMap, mode === "view");
      const w = window.open("", "_blank", "width=1000,height=1200");
      if (!w) { toast.error("Popup blocked"); return; }
      w.document.write(html); w.document.close();
    } catch { toast.error("Could not load full monthly report"); }
    setBusy(false);
  };

  return (
    <div className="space-y-4">
      <div className="rounded-2xl border border-white/10 bg-[#0F0F13] p-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <MonthPicker value={month} onChange={setMonth} />
          <Button data-testid="pnl-generate-btn" onClick={() => fetchReport(month)} className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold" disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <FileText className="h-4 w-4 mr-2" />}
            Generate P&amp;L
          </Button>
        </div>
        <div className="flex flex-wrap gap-2 pt-2 border-t border-white/10">
          <div className="text-[11px] uppercase tracking-widest text-white/40 w-full mb-1">Profit &amp; Loss</div>
          <Button data-testid="pnl-view-btn" variant="outline" onClick={viewReport} disabled={!data} className="border-brand-cyan/40 text-brand-cyan hover:bg-brand-cyan/10">
            <Eye className="h-4 w-4 mr-2" />View P&amp;L
          </Button>
          <Button data-testid="pnl-print-btn" variant="outline" onClick={printReport} disabled={!data} className="border-white/20 text-white hover:bg-white/10">
            <Printer className="h-4 w-4 mr-2" />Print P&amp;L
          </Button>
          <div className="w-full text-[11px] uppercase tracking-widest text-white/40 mt-3 mb-1">Full Monthly Report</div>
          <Button data-testid="monthly-view-btn" variant="outline" onClick={() => openMonthly("view")} disabled={busy} className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10">
            <Eye className="h-4 w-4 mr-2" />View Monthly
          </Button>
          <Button data-testid="monthly-full-report-btn" onClick={() => openMonthly("print")} disabled={busy} className="bg-emerald-500/20 text-emerald-200 border border-emerald-500/40 hover:bg-emerald-500/30 font-semibold">
            <Printer className="h-4 w-4 mr-2" />Print Monthly
          </Button>
        </div>
      </div>

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard label={`Income · ${data.month}`} value={`₹${(data.income_total || 0).toLocaleString("en-IN")}`} accent="cyan" />
            <StatCard label="Expenses" value={`₹${(data.expense_total || 0).toLocaleString("en-IN")}`} accent="rose" />
            <div className={`rounded-2xl border p-4 ${data.profit >= 0 ? "border-emerald-500/40 bg-emerald-500/5" : "border-rose-500/40 bg-rose-500/10"}`} data-testid="pnl-profit-card">
              <div className="text-[11px] uppercase tracking-[0.18em] text-white/50">{data.profit >= 0 ? "Net Profit" : "Net Loss"}</div>
              <div className={`font-heading text-3xl font-extrabold mt-1 ${data.profit >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                ₹{Math.abs(data.profit || 0).toLocaleString("en-IN")}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 text-white/70 text-xs uppercase tracking-widest">Paid invoices</div>
              {data.invoices?.length ? (
                <Table>
                  <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/60">Number</TableHead>
                    <TableHead className="text-white/60">Client</TableHead>
                    <TableHead className="text-white/60 text-right">₹</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.invoices.map((i) => (
                      <TableRow key={i.number} className="border-white/10 hover:bg-white/5">
                        <TableCell className="font-mono text-brand-cyan text-xs">{i.number}</TableCell>
                        <TableCell className="text-white/80">{clientMap[i.client_id]?.name || "—"}<div className="text-[10px] text-white/40">{i.doc_date}</div></TableCell>
                        <TableCell className="text-right text-emerald-300 font-semibold whitespace-nowrap">+₹{(i.total || 0).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <div className="p-6 text-center text-white/40 text-sm">No paid invoices in this month.</div>}
            </div>

            <div className="rounded-2xl border border-white/10 bg-[#0F0F13] overflow-hidden">
              <div className="px-4 py-3 border-b border-white/10 text-white/70 text-xs uppercase tracking-widest">Expenses by category</div>
              {data.expense_by_category?.length ? (
                <Table>
                  <TableHeader><TableRow className="border-white/10 hover:bg-transparent">
                    <TableHead className="text-white/60">Category</TableHead>
                    <TableHead className="text-white/60 text-right">₹</TableHead>
                  </TableRow></TableHeader>
                  <TableBody>
                    {data.expense_by_category.map((e) => (
                      <TableRow key={e.category} className="border-white/10 hover:bg-white/5">
                        <TableCell className="text-white/80">{e.category}</TableCell>
                        <TableCell className="text-right text-rose-300 font-semibold whitespace-nowrap">-₹{(e.amount || 0).toLocaleString("en-IN")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : <div className="p-6 text-center text-white/40 text-sm">No expenses in this month.</div>}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function MonthPicker({ value, onChange, label = "Month", testid = "month-picker" }) {
  // value: "YYYY-MM"
  const [y, m] = (value || "").split("-");
  const now = new Date();
  const years = [];
  for (let i = now.getFullYear() + 1; i >= 2024; i--) years.push(String(i));
  const months = [
    ["01", "January"], ["02", "February"], ["03", "March"], ["04", "April"],
    ["05", "May"], ["06", "June"], ["07", "July"], ["08", "August"],
    ["09", "September"], ["10", "October"], ["11", "November"], ["12", "December"],
  ];
  const setYear = (yy) => onChange(`${yy}-${m || "01"}`);
  const setMonth = (mm) => onChange(`${y || String(now.getFullYear())}-${mm}`);
  return (
    <div>
      <Label className="text-white/80 text-xs">{label}</Label>
      <div className="flex gap-2">
        <Select value={m || ""} onValueChange={setMonth}>
          <SelectTrigger data-testid={`${testid}-month`} className="bg-black/40 border-white/10 w-36"><SelectValue placeholder="Month" /></SelectTrigger>
          <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
            {months.map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={y || ""} onValueChange={setYear}>
          <SelectTrigger data-testid={`${testid}-year`} className="bg-black/40 border-white/10 w-28"><SelectValue placeholder="Year" /></SelectTrigger>
          <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
            {years.map((yy) => <SelectItem key={yy} value={yy}>{yy}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
    </div>
  );
}

function ClientViewDialog({ open, client, invoices = [], onOpenChange }) {
  if (!client) return null;
  const rows = invoices.filter((i) => i.client_id === client.id);
  const paidTotal = rows.filter((i) => i.status === "paid").reduce((a, b) => a + (b.total || 0), 0);
  const dueTotal = rows.filter((i) => i.status !== "paid").reduce((a, b) => a + (b.total || 0), 0);
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="client-view-dialog" className="bg-[#0F0F13] border-white/10 text-white sm:max-w-lg max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{client.name}</DialogTitle>
          <DialogDescription className="text-white/50">Client details &amp; billing history</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field k="Phone" v={client.phone} mono />
          <Field k="Email" v={client.email} />
          <Field k="Plan" v={client.plan || "—"} />
          <Field k="Monthly ₹" v={`₹${(client.monthly_amount || 0).toLocaleString("en-IN")}`} />
          <div className="col-span-2"><Field k="Address" v={client.address || "—"} /></div>
          <div className="col-span-2"><Field k="Notes" v={client.notes || "—"} /></div>
        </div>
        <div className="rounded-lg border border-white/10 bg-black/30 p-3 text-sm">
          <div className="text-[11px] uppercase tracking-widest text-white/40 mb-2">Billing history · {rows.length} invoice{rows.length === 1 ? "" : "s"}</div>
          {rows.length ? (
            <div className="space-y-1 max-h-40 overflow-y-auto">
              {rows.map((i) => (
                <div key={i.id} className="flex items-center justify-between text-xs">
                  <span className="font-mono text-brand-cyan">{i.number}</span>
                  <span className="text-white/60">{i.doc_date}</span>
                  <span className={`px-2 py-0.5 rounded-full text-[10px] ${i.status === "paid" ? "bg-emerald-500/15 text-emerald-300" : i.status === "overdue" ? "bg-rose-500/15 text-rose-300" : "bg-white/10 text-white/70"}`}>{i.status}</span>
                  <span className="text-white font-semibold">₹{(i.total || 0).toLocaleString("en-IN")}</span>
                </div>
              ))}
            </div>
          ) : <div className="text-white/40 text-xs text-center py-3">No invoices yet.</div>}
          <div className="flex justify-between mt-3 pt-3 border-t border-white/10 text-xs">
            <span>Paid: <b className="text-emerald-400">₹{paidTotal.toLocaleString("en-IN")}</b></span>
            <span>Outstanding: <b className="text-brand-cyan">₹{dueTotal.toLocaleString("en-IN")}</b></span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ k, v, mono }) {
  return <div><div className="text-[10px] uppercase tracking-widest text-white/40">{k}</div><div className={`text-white/90 ${mono ? "font-mono" : ""}`}>{v || "—"}</div></div>;
}

function monthLabel(m) {
  // "2026-07" -> "July 2026"
  if (!m || m.length < 7) return m || "";
  const [y, mm] = m.split("-");
  const names = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  return `${names[parseInt(mm, 10) - 1] || mm} ${y}`;
}

function ExpenseViewDialog({ open, expense, onOpenChange }) {
  if (!expense) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="expense-view-dialog" className="bg-[#0F0F13] border-white/10 text-white sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Expense · {expense.category}</DialogTitle>
          <DialogDescription className="text-white/50">Recorded on {expense.date}</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3 text-sm">
          <Field k="Amount" v={`₹${(expense.amount || 0).toLocaleString("en-IN")}`} />
          <Field k="Category" v={expense.category} />
          <Field k="Staff" v={expense.staff || "—"} />
          <Field k="Paid via" v={expense.paid_via || "—"} />
          <div className="col-span-2"><Field k="Note" v={expense.note || "—"} /></div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function RepeatExpensesDialog({ open, expenses = [], onOpenChange, onSubmit }) {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [sourceMonth, setSourceMonth] = useState(thisMonth);
  const [targetDate, setTargetDate] = useState(new Date().toISOString().slice(0, 10));
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    setSourceMonth(thisMonth);
    setTargetDate(new Date().toISOString().slice(0, 10));
    setSelected({});
    // eslint-disable-next-line
  }, [open]);

  const monthRows = expenses.filter((e) => (e.date || "").startsWith(sourceMonth));
  const allChecked = monthRows.length > 0 && monthRows.every((e) => selected[e.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(monthRows.map((e) => [e.id, true])));
  };
  const submit = () => {
    const ids = monthRows.filter((e) => selected[e.id]).map((e) => e.id);
    if (!ids.length) return toast.error("Select at least one expense");
    onSubmit({ source_ids: ids, target_date: targetDate });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="repeat-expenses-dialog" className="bg-[#0F0F13] border-white/10 text-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Repeat expenses for a new month</DialogTitle>
          <DialogDescription className="text-white/50">
            Duplicate recurring expenses (salaries, rent, internet…) from any month with one click.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <MonthPicker value={sourceMonth} onChange={setSourceMonth} label="Source month" testid="exp-repeat-source" />
          <FormRow label="Post to date">
            <Input data-testid="exp-repeat-target-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className="bg-black/40 border-white/10" />
          </FormRow>
        </div>

        <div className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs">
            <span className="uppercase tracking-widest text-white/50">
              {monthRows.length} expense{monthRows.length === 1 ? "" : "s"} found in {sourceMonth}
            </span>
            <button type="button" onClick={toggleAll} data-testid="exp-repeat-select-all" className="text-brand-cyan hover:underline">
              {allChecked ? "Unselect all" : "Select all"}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {monthRows.length === 0 && <div className="text-center text-white/40 text-sm py-6">No expenses in that month.</div>}
            {monthRows.map((e) => (
              <label key={e.id} data-testid={`exp-repeat-row-${e.id}`} className="flex items-center gap-3 px-3 py-2 border-b border-white/5 last:border-b-0 hover:bg-white/5 cursor-pointer">
                <input type="checkbox" checked={!!selected[e.id]} onChange={(ev) => setSelected({ ...selected, [e.id]: ev.target.checked })} className="accent-brand-cyan h-4 w-4" />
                <div className="flex-1 grid grid-cols-3 sm:grid-cols-4 gap-2 items-center text-sm">
                  <span className="text-white/70 text-xs">{e.date}</span>
                  <span className="text-white font-semibold">{e.category}</span>
                  <span className="text-white/50 text-xs hidden sm:inline">{e.staff || e.note || ""}</span>
                  <span className="text-right text-rose-300 font-semibold">-₹{(e.amount || 0).toLocaleString("en-IN")}</span>
                </div>
              </label>
            ))}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={submit} data-testid="exp-repeat-submit-btn" className="bg-emerald-500/25 border border-emerald-500/50 text-emerald-100 hover:bg-emerald-500/35 font-bold">
            <RotateCcw className="h-4 w-4 mr-2" />Duplicate selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function RepeatMonthDialog({ open, kind, docs = [], clients = {}, onOpenChange, onSubmit }) {
  const now = new Date();
  const thisMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [sourceMonth, setSourceMonth] = useState(thisMonth);
  const [targetDate, setTargetDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [selected, setSelected] = useState({});

  useEffect(() => {
    if (!open) return;
    setSourceMonth(thisMonth);
    setTargetDate(new Date().toISOString().slice(0, 10));
    setDueDate("");
    setSelected({});
    // eslint-disable-next-line
  }, [open]);

  const monthDocs = docs.filter((d) => (d.doc_date || "").startsWith(sourceMonth));
  const allChecked = monthDocs.length > 0 && monthDocs.every((d) => selected[d.id]);
  const toggleAll = () => {
    if (allChecked) setSelected({});
    else setSelected(Object.fromEntries(monthDocs.map((d) => [d.id, true])));
  };
  const submit = () => {
    const ids = monthDocs.filter((d) => selected[d.id]).map((d) => d.id);
    if (!ids.length) return toast.error("Select at least one to repeat");
    onSubmit({ source_ids: ids, target_date: targetDate, target_due_date: dueDate || null });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="repeat-dialog" className="bg-[#0F0F13] border-white/10 text-white sm:max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Repeat {kind}s for a new month</DialogTitle>
          <DialogDescription className="text-white/50">
            Pick a source month, choose which {kind}s to duplicate, then set the new date.
            Copies are created as <b>drafts</b> with fresh numbers, same client &amp; items.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <MonthPicker value={sourceMonth} onChange={setSourceMonth} label="Source month" testid="repeat-source" />
          <FormRow label={`New ${kind} date`}>
            <Input data-testid="repeat-target-date" type="date" value={targetDate} onChange={(e) => setTargetDate(e.target.value)} className="bg-black/40 border-white/10" />
          </FormRow>
          {kind === "invoice" && (
            <FormRow label="Due date (optional)">
              <Input data-testid="repeat-due-date" type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} className="bg-black/40 border-white/10" />
            </FormRow>
          )}
        </div>

        <div className="rounded-lg border border-white/10 bg-black/30 overflow-hidden">
          <div className="px-3 py-2 border-b border-white/10 flex items-center justify-between text-xs">
            <span className="uppercase tracking-widest text-white/50">
              {monthDocs.length} {kind}{monthDocs.length === 1 ? "" : "s"} found in {sourceMonth}
            </span>
            <button type="button" onClick={toggleAll} data-testid="repeat-select-all" className="text-brand-cyan hover:underline">
              {allChecked ? "Unselect all" : "Select all"}
            </button>
          </div>
          <div className="max-h-64 overflow-y-auto">
            {monthDocs.length === 0 && <div className="text-center text-white/40 text-sm py-6">No {kind}s in that month.</div>}
            {monthDocs.map((d) => {
              const c = clients[d.client_id] || {};
              return (
                <label key={d.id} data-testid={`repeat-row-${d.id}`} className="flex items-center gap-3 px-3 py-2 border-b border-white/5 last:border-b-0 hover:bg-white/5 cursor-pointer">
                  <input type="checkbox" checked={!!selected[d.id]} onChange={(e) => setSelected({ ...selected, [d.id]: e.target.checked })} className="accent-brand-cyan h-4 w-4" />
                  <div className="flex-1 grid grid-cols-3 sm:grid-cols-4 gap-2 items-center text-sm">
                    <span className="font-mono text-brand-cyan text-xs">{d.number}</span>
                    <span className="text-white truncate">{c.name || "—"}</span>
                    <span className="text-white/60 text-xs hidden sm:inline">{d.doc_date}</span>
                    <span className="text-right text-white font-semibold">₹{(d.total || 0).toLocaleString("en-IN")}</span>
                  </div>
                </label>
              );
            })}
          </div>
        </div>

        <DialogFooter>
          <Button type="button" onClick={submit} data-testid="repeat-submit-btn" className="bg-emerald-500/25 border border-emerald-500/50 text-emerald-100 hover:bg-emerald-500/35 font-bold">
            <RotateCcw className="h-4 w-4 mr-2" />Duplicate selected
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function renderPnLHtml(data, clientMap = {}, forCanvas = false) {
  const invRows = (data.invoices || []).map((i) =>
    `<tr><td>${esc(i.number)}</td><td>${esc(clientMap[i.client_id]?.name || "—")}</td><td class="c">${esc(i.doc_date)}</td><td class="r">₹${(i.total || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const expRows = (data.expenses || []).map((e) =>
    `<tr><td>${esc(e.date)}</td><td>${esc(e.category)}</td><td>${esc(e.note || "")}</td><td class="r">₹${(e.amount || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const catRows = (data.expense_by_category || []).map((c) =>
    `<tr><td>${esc(c.category)}</td><td class="r">₹${(c.amount || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  return `<!doctype html><html><head><meta charset="utf-8"/><title>P&amp;L — ${esc(data.month)}</title>
  <style>
    *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif}
    body{margin:32px;color:#111}
    h1{font-size:24px;margin:0 0 4px}
    .sub{color:#666;font-size:12px;margin-bottom:20px}
    .cards{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin:14px 0 22px}
    .card{border:1px solid #eee;border-radius:10px;padding:14px}
    .card .l{font-size:10px;letter-spacing:1.5px;color:#888;text-transform:uppercase}
    .card .v{font-size:22px;font-weight:800;margin-top:4px}
    .card.p{background:#ecfdf5;border-color:#a7f3d0}
    .card.n{background:#fef2f2;border-color:#fecaca}
    table{border-collapse:collapse;width:100%;margin-top:6px;margin-bottom:20px}
    th,td{border:1px solid #eee;padding:8px 10px;font-size:12px}
    th{background:#f6f6f6;text-align:left;text-transform:uppercase;font-size:10px;letter-spacing:1.5px;color:#666}
    td.r{text-align:right}td.c{text-align:center}
    h2{font-size:13px;text-transform:uppercase;letter-spacing:1.5px;color:#555;margin:16px 0 6px}
    .foot{margin-top:20px;font-size:11px;color:#888}
  </style></head><body>
  <h1>Profit &amp; Loss · ${esc(data.month)}</h1>
  <div class="sub">SECURE WATCH · A Smart Security Solution · Guwahati</div>
  <div class="cards">
    <div class="card"><div class="l">Income</div><div class="v">₹${(data.income_total || 0).toLocaleString("en-IN")}</div></div>
    <div class="card"><div class="l">Expenses</div><div class="v">₹${(data.expense_total || 0).toLocaleString("en-IN")}</div></div>
    <div class="card ${data.profit >= 0 ? 'p' : 'n'}"><div class="l">${data.profit >= 0 ? 'Net Profit' : 'Net Loss'}</div><div class="v">₹${Math.abs(data.profit || 0).toLocaleString("en-IN")}</div></div>
  </div>
  <h2>Paid Invoices</h2>
  <table><thead><tr><th>Number</th><th>Client</th><th class="c">Date</th><th class="r">Amount</th></tr></thead><tbody>${invRows || '<tr><td colspan="4" style="text-align:center;color:#999">No paid invoices</td></tr>'}</tbody></table>
  <h2>Expenses by Category</h2>
  <table><thead><tr><th>Category</th><th class="r">Amount</th></tr></thead><tbody>${catRows || '<tr><td colspan="2" style="text-align:center;color:#999">—</td></tr>'}</tbody></table>
  <h2>Expense Details</h2>
  <table><thead><tr><th>Date</th><th>Category</th><th>Note</th><th class="r">Amount</th></tr></thead><tbody>${expRows || '<tr><td colspan="4" style="text-align:center;color:#999">No expenses</td></tr>'}</tbody></table>
  <p class="foot">Generated ${new Date().toLocaleString("en-IN")}</p>
  ${forCanvas ? "" : `<script>window.onload=()=>{setTimeout(()=>window.print(),250);}</script>`}
  </body></html>`;
}

function renderMonthlyHtml(data, clientMap = {}, forCanvas = false) {
  const t = data.totals || {};
  const invStatus = data.invoice_by_status || [];
  const invRows = (data.invoices || []).map((i) =>
    `<tr><td>${esc(i.number)}</td><td>${esc(clientMap[i.client_id]?.name || "—")}</td><td class="c">${esc(i.doc_date)}</td><td class="c">${esc(i.due_date || "—")}</td><td class="c">${esc(i.status || "")}</td><td class="r">₹${(i.total || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const estRows = (data.estimates || []).map((i) =>
    `<tr><td>${esc(i.number)}</td><td>${esc(clientMap[i.client_id]?.name || "—")}</td><td class="c">${esc(i.doc_date)}</td><td class="c">${esc(i.status || "")}</td><td class="r">₹${(i.total || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const catRows = (data.expense_by_category || []).map((c) =>
    `<tr><td>${esc(c.category)}</td><td class="r">₹${(c.amount || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const staffRows = (data.expense_by_staff || []).map((s) =>
    `<tr><td>${esc(s.staff)}</td><td class="r">₹${(s.amount || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const expRows = (data.expenses || []).map((e) =>
    `<tr><td>${esc(e.date)}</td><td>${esc(e.category)}</td><td>${esc(e.staff || "—")}</td><td>${esc(e.paid_via || "—")}</td><td>${esc(e.note || "")}</td><td class="r">₹${(e.amount || 0).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const leadRows = (data.leads || []).map((l) =>
    `<tr><td>${esc(l.ticket_number || "")}</td><td>${esc(l.name || "")}</td><td>${esc(l.phone || "")}</td><td>${esc(l.property_type || "—")}</td><td class="c">${esc(l.status || "")}</td></tr>`
  ).join("");
  const statusChips = invStatus.map((s) => `<span style="background:#eef;color:#334;padding:2px 8px;border-radius:99px;font-size:10px;text-transform:uppercase;letter-spacing:1px;margin-right:6px">${esc(s.status)} · ${s.count}</span>`).join("");
  const profitOk = (t.profit || 0) >= 0;

  return `<!doctype html><html><head><meta charset="utf-8"/><title>Monthly Report — ${esc(data.month)}</title>
  <style>
    *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif}
    body{margin:28px;color:#111}
    h1{font-size:24px;margin:0 0 4px}
    .sub{color:#666;font-size:12px;margin-bottom:16px}
    .cards{display:grid;grid-template-columns:repeat(5,1fr);gap:10px;margin:10px 0 18px}
    .card{border:1px solid #eee;border-radius:10px;padding:12px}
    .card .l{font-size:9px;letter-spacing:1.5px;color:#888;text-transform:uppercase}
    .card .v{font-size:18px;font-weight:800;margin-top:4px}
    .card.p{background:#ecfdf5;border-color:#a7f3d0}
    .card.n{background:#fef2f2;border-color:#fecaca}
    .card.b{background:#eff6ff;border-color:#bfdbfe}
    .grid2{display:grid;grid-template-columns:1fr 1fr;gap:14px}
    table{border-collapse:collapse;width:100%;margin-top:6px;margin-bottom:18px}
    th,td{border:1px solid #eee;padding:6px 8px;font-size:11px}
    th{background:#f6f6f6;text-align:left;text-transform:uppercase;font-size:9px;letter-spacing:1.5px;color:#666}
    td.r{text-align:right}td.c{text-align:center}
    h2{font-size:12px;text-transform:uppercase;letter-spacing:1.5px;color:#555;margin:14px 0 4px;border-bottom:1px solid #eee;padding-bottom:4px}
    .foot{margin-top:18px;font-size:10px;color:#888}
    .kpis{margin:6px 0 10px;font-size:11px;color:#333}
    @media print{@page{size:A4;margin:12mm}}
  </style></head><body>
  <h1>SECURE WATCH · Monthly Report</h1>
  <div class="sub">${esc(data.month)} · A Smart Security Solution · Guwahati, Assam</div>
  <div class="cards">
    <div class="card"><div class="l">Invoiced</div><div class="v">₹${(t.invoiced || 0).toLocaleString("en-IN")}</div><div class="kpis">${t.invoice_count || 0} invoices</div></div>
    <div class="card p"><div class="l">Paid</div><div class="v">₹${(t.paid || 0).toLocaleString("en-IN")}</div></div>
    <div class="card n"><div class="l">Outstanding</div><div class="v">₹${(t.outstanding || 0).toLocaleString("en-IN")}</div></div>
    <div class="card"><div class="l">Expense</div><div class="v">₹${(t.expense || 0).toLocaleString("en-IN")}</div><div class="kpis">${t.expense_count || 0} entries</div></div>
    <div class="card ${profitOk ? 'p' : 'n'}"><div class="l">${profitOk ? 'Net Profit' : 'Net Loss'}</div><div class="v">₹${Math.abs(t.profit || 0).toLocaleString("en-IN")}</div></div>
  </div>
  <div class="kpis">Estimates issued: <b>${t.estimate_count || 0}</b> · New leads captured: <b>${t.lead_count || 0}</b> · Invoice status: ${statusChips || '<span style="color:#999">—</span>'}</div>

  <h2>Invoices</h2>
  <table><thead><tr><th>Number</th><th>Client</th><th class="c">Date</th><th class="c">Due</th><th class="c">Status</th><th class="r">Total</th></tr></thead><tbody>${invRows || '<tr><td colspan="6" style="text-align:center;color:#999">No invoices this month</td></tr>'}</tbody></table>

  <h2>Estimates</h2>
  <table><thead><tr><th>Number</th><th>Client</th><th class="c">Date</th><th class="c">Status</th><th class="r">Total</th></tr></thead><tbody>${estRows || '<tr><td colspan="5" style="text-align:center;color:#999">No estimates this month</td></tr>'}</tbody></table>

  <div class="grid2">
    <div>
      <h2>Expense by Category</h2>
      <table><thead><tr><th>Category</th><th class="r">Amount</th></tr></thead><tbody>${catRows || '<tr><td colspan="2" style="text-align:center;color:#999">—</td></tr>'}</tbody></table>
    </div>
    <div>
      <h2>Expense by Staff</h2>
      <table><thead><tr><th>Staff</th><th class="r">Amount</th></tr></thead><tbody>${staffRows || '<tr><td colspan="2" style="text-align:center;color:#999">—</td></tr>'}</tbody></table>
    </div>
  </div>

  <h2>Expense Details</h2>
  <table><thead><tr><th>Date</th><th>Category</th><th>Staff</th><th>Paid via</th><th>Note</th><th class="r">Amount</th></tr></thead><tbody>${expRows || '<tr><td colspan="6" style="text-align:center;color:#999">No expenses this month</td></tr>'}</tbody></table>

  <h2>New Leads Captured</h2>
  <table><thead><tr><th>Ticket</th><th>Name</th><th>Phone</th><th>Property</th><th class="c">Status</th></tr></thead><tbody>${leadRows || '<tr><td colspan="5" style="text-align:center;color:#999">No leads this month</td></tr>'}</tbody></table>

  <p class="foot">Generated ${new Date().toLocaleString("en-IN")} · SECURE WATCH Admin · Confidential</p>
  ${forCanvas ? "" : `<script>window.onload=()=>{setTimeout(()=>window.print(),300);}</script>`}
  </body></html>`;
}

function DocDialog({ open, onOpenChange, kind, clients, catalog = [], editing, onSave }) {
  const today = new Date().toISOString().slice(0, 10);
  const [client_id, setClient] = useState("");
  const [doc_date, setDate] = useState(today);
  const [due_date, setDue] = useState("");
  const [items, setItems] = useState([{ description: "Monthly monitoring service", qty: 1, rate: 0 }]);
  const [tax_pct, setTax] = useState(18);
  const [notes, setNotes] = useState("");
  const [status, setStatus] = useState("draft");

  useEffect(() => {
    if (!open) return;
    if (editing) {
      setClient(editing.client_id); setDate(editing.doc_date); setDue(editing.due_date || "");
      setItems(editing.items?.length ? editing.items : [{ description: "", qty: 1, rate: 0 }]);
      setTax(editing.tax_pct || 0); setNotes(editing.notes || ""); setStatus(editing.status || "draft");
    } else {
      setClient(clients[0]?.id || ""); setDate(today); setDue("");
      setItems([{ description: kind === "invoice" ? "Monthly CCTV Remote Monitoring" : "Estimate item", qty: 1, rate: 0 }]);
      setTax(18); setNotes(""); setStatus("draft");
    }
    // eslint-disable-next-line
  }, [open, editing, kind]);

  const subtotal = items.reduce((a, i) => a + (Number(i.qty) || 0) * (Number(i.rate) || 0), 0);
  const tax = Math.round(subtotal * tax_pct / 100 * 100) / 100;
  const total = subtotal + tax;

  const save = (e) => {
    e.preventDefault();
    if (!client_id) return toast.error("Select a client");
    onSave({
      kind, client_id, doc_date, due_date: due_date || null,
      items: items.map((i) => ({ description: i.description || "Item", qty: Number(i.qty) || 0, rate: Number(i.rate) || 0 })),
      tax_pct: Number(tax_pct) || 0, notes, status,
    }, editing?.id);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#0F0F13] border-white/10 text-white sm:max-w-3xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="capitalize">{editing ? `Edit ${kind}` : `New ${kind}`}</DialogTitle>
          <DialogDescription>Fill line items — subtotal, tax and total are calculated automatically.</DialogDescription>
        </DialogHeader>
        <form onSubmit={save} className="grid gap-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormRow label="Client">
              <Select value={client_id} onValueChange={setClient}>
                <SelectTrigger className="bg-black/40 border-white/10"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                  {clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FormRow>
            <FormRow label="Date"><Input type="date" value={doc_date} onChange={(e) => setDate(e.target.value)} className="bg-black/40 border-white/10" /></FormRow>
            {kind === "invoice" && <FormRow label="Due date"><Input type="date" value={due_date} onChange={(e) => setDue(e.target.value)} className="bg-black/40 border-white/10" /></FormRow>}
          </div>

          <div className="rounded-lg border border-white/10 bg-black/30 p-3">
            <div className="grid grid-cols-12 gap-2 text-[11px] uppercase tracking-widest text-white/40 mb-2">
              <div className="col-span-7">Description</div>
              <div className="col-span-1 text-center">Qty</div>
              <div className="col-span-2 text-right">Rate ₹</div>
              <div className="col-span-2 text-right">Amount</div>
            </div>
            {items.map((it, idx) => (
              <div key={idx} className="grid grid-cols-12 gap-2 mb-2 items-center">
                {catalog.length > 0 && (
                  <div className="col-span-12 -mb-1">
                    <Select
                      value=""
                      onValueChange={(catId) => {
                        const cat = catalog.find((x) => x.id === catId);
                        if (!cat) return;
                        setItems(items.map((x, i) => i === idx ? { ...x, description: cat.name + (cat.unit ? ` (${cat.unit})` : ""), rate: cat.default_rate || x.rate } : x));
                      }}
                    >
                      <SelectTrigger data-testid={`catalog-picker-${idx}`} className="bg-black/40 border-white/10 h-8 text-xs">
                        <SelectValue placeholder="⚡ Quick pick from catalog…" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#0F0F13] border-white/10 text-white">
                        {catalog.map((cat) => (
                          <SelectItem key={cat.id} value={cat.id}>
                            {cat.name} — ₹{(cat.default_rate || 0).toLocaleString("en-IN")}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Input className="col-span-7 bg-black/40 border-white/10" value={it.description}
                  onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, description: e.target.value } : x))} />
                <Input type="number" className="col-span-1 bg-black/40 border-white/10 text-center" value={it.qty}
                  onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, qty: e.target.value } : x))} />
                <Input type="number" className="col-span-2 bg-black/40 border-white/10 text-right" value={it.rate}
                  onChange={(e) => setItems(items.map((x, i) => i === idx ? { ...x, rate: e.target.value } : x))} />
                <div className="col-span-1 text-right text-white/85 text-sm">₹{((Number(it.qty)||0)*(Number(it.rate)||0)).toLocaleString("en-IN")}</div>
                <button type="button" onClick={() => setItems(items.filter((_, i) => i !== idx))} className="col-span-1 text-rose-400 hover:text-rose-300"><Trash2 className="h-4 w-4 mx-auto" /></button>
              </div>
            ))}
            <button type="button" onClick={() => setItems([...items, { description: "", qty: 1, rate: 0 }])} className="text-xs text-brand-cyan hover:underline mt-1"><Plus className="h-3 w-3 inline mr-1" />Add line item</button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <FormRow label="Tax %"><Input type="number" value={tax_pct} onChange={(e) => setTax(e.target.value)} className="bg-black/40 border-white/10" /></FormRow>
            <div className="sm:col-span-2 rounded-lg border border-white/10 bg-black/30 p-3 text-sm space-y-1">
              <div className="flex justify-between text-white/60"><span>Subtotal</span><span>₹{subtotal.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-white/60"><span>Tax ({tax_pct}%)</span><span>₹{tax.toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-white font-bold text-base pt-1 border-t border-white/10"><span>Total</span><span className="text-brand-cyan">₹{total.toLocaleString("en-IN")}</span></div>
            </div>
          </div>

          <FormRow label="Notes"><Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} className="bg-black/40 border-white/10" /></FormRow>

          <DialogFooter><Button type="submit" className="bg-brand-cyan text-black hover:bg-brand-cyan/90 font-bold">{editing ? "Update" : "Create"} {kind}</Button></DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------- Printable HTML ----------
function renderDocHtml(d, c, upi = {}, forCanvas = false) {
  const rows = (d.items || []).map((i) =>
    `<tr><td>${esc(i.description)}</td><td class="c">${i.qty}</td><td class="r">₹${(i.rate || 0).toLocaleString("en-IN")}</td><td class="r">₹${((i.qty || 0) * (i.rate || 0)).toLocaleString("en-IN")}</td></tr>`
  ).join("");
  const title = d.kind === "invoice" ? "INVOICE" : "ESTIMATE";
  const upiVpa = upi?.upi_vpa || "";
  const upiName = upi?.upi_name || "SECURE WATCH";
  const upiUri = upiVpa
    ? `upi://pay?pa=${encodeURIComponent(upiVpa)}&pn=${encodeURIComponent(upiName)}&am=${encodeURIComponent(d.total || 0)}&cu=INR&tn=${encodeURIComponent(d.number || "")}`
    : "";
  const qrImg = upiUri
    ? `https://api.qrserver.com/v1/create-qr-code/?size=180x180&data=${encodeURIComponent(upiUri)}`
    : "";
  const payBlock = d.kind === "invoice" && upiVpa
    ? `<div style="margin-top:22px;display:flex;gap:16px;align-items:center;border:1px dashed #bbb;padding:14px;border-radius:10px;">
         <img src="${qrImg}" alt="UPI QR" width="150" height="150" style="border-radius:6px;background:#fff"/>
         <div style="font-size:12px;color:#222">
           <div style="font-size:11px;letter-spacing:1.5px;color:#888;text-transform:uppercase;margin-bottom:4px">Pay via UPI</div>
           <div style="font-weight:800;font-size:16px">₹${(d.total || 0).toLocaleString("en-IN")}</div>
           <div>UPI ID: <b>${esc(upiVpa)}</b></div>
           <div>Payee: ${esc(upiName)}</div>
           <div style="color:#666;margin-top:6px">Scan the QR with any UPI app (GPay / PhonePe / Paytm)</div>
         </div>
       </div>`
    : "";
  const autoPrint = forCanvas ? "" : `<script>window.onload=()=>{setTimeout(()=>window.print(),200);}</script>`;
  return `<!doctype html><html><head><meta charset="utf-8"/><title>${title} — ${esc(d.number)}</title>
    <style>
      *{box-sizing:border-box;font-family:-apple-system,BlinkMacSystemFont,Segoe UI,Arial,sans-serif}
      body{margin:32px;color:#111}
      .brand{display:flex;justify-content:space-between;align-items:center;border-bottom:2px solid #111;padding-bottom:16px;margin-bottom:20px}
      .brand img{height:52px}
      .biz h1{font-size:22px;margin:0}
      .biz p{margin:2px 0;font-size:12px;color:#555}
      .meta{margin-top:10px;font-size:12px;color:#333}
      .title{font-size:26px;font-weight:800;letter-spacing:2px;color:#111}
      .cols{display:grid;grid-template-columns:1fr 1fr;gap:20px;margin:20px 0}
      .cols h4{margin:0 0 6px;font-size:11px;letter-spacing:1.5px;color:#888;text-transform:uppercase}
      table{border-collapse:collapse;width:100%;margin-top:10px}
      th,td{border:1px solid #eee;padding:10px 12px;font-size:13px}
      th{background:#f6f6f6;text-align:left;font-size:11px;text-transform:uppercase;letter-spacing:1.5px;color:#666}
      td.r{text-align:right}td.c{text-align:center}
      .totals{margin-top:14px;margin-left:auto;width:280px;font-size:13px}
      .totals div{display:flex;justify-content:space-between;padding:6px 0;border-bottom:1px dashed #eee}
      .totals .tot{font-weight:800;font-size:16px;border-top:2px solid #111;border-bottom:none;padding-top:10px}
      .status{display:inline-block;padding:4px 10px;border-radius:99px;background:#eef;color:#334;font-size:11px;font-weight:700;text-transform:uppercase;letter-spacing:1px}
      .foot{margin-top:22px;font-size:11px;color:#888}
    </style></head><body>
    <div class="brand">
      <div class="biz"><img src="${BRAND.logoUrl}" alt=""/></div>
      <div style="text-align:right"><div class="title">${title}</div><div class="meta"><b>${esc(d.number)}</b></div><div class="meta">Date: ${esc(d.doc_date)}</div>${d.due_date ? `<div class="meta">Due: ${esc(d.due_date)}</div>` : ""}</div>
    </div>
    <div class="cols">
      <div><h4>From</h4><b>SECURE WATCH</b><br/><small>A Smart Security Solution</small><br/>${esc(BRAND.address)}<br/>Phone: ${esc(BRAND.phoneDisplay)}<br/>Email: ${esc(BRAND.email)}</div>
      <div><h4>Billed to</h4><b>${esc(c.name || "—")}</b><br/>${c.address ? esc(c.address) + "<br/>" : ""}${c.phone ? "Phone: " + esc(c.phone) + "<br/>" : ""}${c.email ? "Email: " + esc(c.email) : ""}</div>
    </div>
    <table><thead><tr><th>Description</th><th>Qty</th><th class="r">Rate</th><th class="r">Amount</th></tr></thead><tbody>${rows}</tbody></table>
    <div class="totals">
      <div><span>Subtotal</span><span>₹${(d.subtotal || 0).toLocaleString("en-IN")}</span></div>
      <div><span>Tax (${d.tax_pct || 0}%)</span><span>₹${(d.tax_amount || 0).toLocaleString("en-IN")}</span></div>
      <div class="tot"><span>Total</span><span>₹${(d.total || 0).toLocaleString("en-IN")}</span></div>
    </div>
    <div style="margin-top:18px"><span class="status">${esc(d.status || "draft")}</span></div>
    ${payBlock}
    ${d.notes ? `<div style="margin-top:20px"><h4 style="margin:0 0 6px;font-size:11px;letter-spacing:1.5px;color:#888;text-transform:uppercase">Notes</h4><div style="font-size:12px;color:#333;white-space:pre-wrap">${esc(d.notes)}</div></div>` : ""}
    <p class="foot">Generated from Secure Watch Admin · ${new Date().toLocaleString("en-IN")}</p>
    ${autoPrint}
  </body></html>`;
}
function esc(s) { return String(s ?? "").replace(/[&<>"']/g, (c) => ({"&":"&amp;","<":"&lt;",">":"&gt;",'"':"&quot;","'":"&#39;"}[c])); }
